import { useAtomValue } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  buildFiveSegmentsTableInfo,
  type FiveSegmentColumnKey,
} from '~/apps/ninesegsAnalyzer/utils/standardAnalysis'
import { useHighlightCells } from '~/hooks/useHighlightCells'
import { useSampleSize } from '~/hooks/useSampleSize'
import { type Brand } from '~/hooks/useSurveyQuery'
import { useTamRatio } from '~/hooks/useTamRatio'
import { type RowInfo } from '~/utils'

import {
  dataDisplayTypeAtom,
  fiveSegmentSortValueAtom,
  hiddenRowIdsAtom,
} from '../atoms'

import { useFiveSegmentsAnswersArrayData } from './useFiveSegmentsAnswersArrayData'
import { useFiveSegmentsCustomerAttributesArrayData } from './useFiveSegmentsCustomerAttributesArrayData'
import { useFiveSegmentsTotalData } from './useFiveSegmentsTotalData'

export const useFiveSegmentsTableInfo = (
  brand: Brand,
): RowInfo<FiveSegmentColumnKey>[] => {
  const { t } = useTranslation()
  const sampleSize = useSampleSize()
  const tamRatio = useTamRatio()

  const dataDisplayType = useAtomValue(dataDisplayTypeAtom)
  const sortValue = useAtomValue(fiveSegmentSortValueAtom)
  const hiddenRowIds = useAtomValue(hiddenRowIdsAtom)

  const totalData = useFiveSegmentsTotalData(brand)
  const answersArrayData = useFiveSegmentsAnswersArrayData(brand)
  const customerAttributesArrayData =
    useFiveSegmentsCustomerAttributesArrayData(brand)

  const { addHighlightProps } = useHighlightCells(brand.name)

  return React.useMemo(
    () =>
      buildFiveSegmentsTableInfo({
        addHighlightProps,
        answersArrayData,
        customerAttributesArrayData,
        dataDisplayType,
        hiddenRowIds,
        sampleSize,
        sortValue,
        t,
        tamRatio,
        totalData: { counts: totalData.counts, ratios: totalData.ratios },
      }),
    [
      addHighlightProps,
      answersArrayData,
      customerAttributesArrayData,
      dataDisplayType,
      hiddenRowIds,
      sampleSize,
      sortValue,
      t,
      tamRatio,
      totalData.counts,
      totalData.ratios,
    ],
  )
}
